import { gql } from '@apollo/client';

export const GET_VALUATION_BY_ID_QUERY = gql`
    query getValuationById($id: Int!){
        valuation(id: $id) {
            id
            valuationAttachments {
              fileName
              documentLocation
            }
            bank {
                id
                name
            }
            organisation {
                id
                name
                address
            }
            office {
                id
                name
                phone
                email
                address
                latitude
                longitude
            }
            company {
                id
                name
                offices {
                    id
                    name
                    address
                    latitude
                    longitude
                }
            }
            requestedBy {
                id
                firstName
                lastName
            }
            underwriter {
                id
                firstName
                lastName
            }
            customerName
            customerEmail
            customerPhone
            securityAddress
            valuationHistoryList {
              id
              content
              status
              contentType
              contentBy
              createdDate
            }
            valuer {
                id
                role
                firstName
                lastName
                phone
                email
            }
            valuationType
            formType
            propertyLocationLatitude
            propertyLocationLongitude
            propertyAddress
            propertyNeighborhood
            propertyCity
            propertyRegion
            eircode
            propertyCaseNumber
            propertyImageLinkUrl
            overdueStatus
            propertyImage {
                documentLocation
                filename
            }
            completedValuationDocument {
                documentLocation
                filename
              }
            dueDate
            status
            valuationAmount
            valuationCurrency
            broker
            brokerEmail
            notifyBroker
            sellingAgent
            sellingAgentEmail
            sellingAgentPhone
            expectedMarketValue
            inspectionDate
            notes
            slaPauseComment
            slaPaused
        }
    }
`

// Driving route (by road) from the assigned office to the property, plus a round-trip fuel
// estimate. Null until an office is assigned; the backend caches the route per office.
export const VALUATION_TRAVEL_ESTIMATE = gql`
    query valuationTravelEstimate($valuationId: Int!) {
        valuationTravelEstimate(valuationId: $valuationId) {
            valuationId
            officeId
            officeName
            officeLatitude
            officeLongitude
            propertyLatitude
            propertyLongitude
            distanceKm
            durationMinutes
            roundTripKm
            fuelLitres
            fuelPricePerLitre
            litresPerHundredKm
            fuelCost
            currency
        }
    }
`;

export const CREATE_VALUATION = gql`
    mutation createValuation($input: ValuationCreate!) {
        createValuation(input: $input) {
            id
        }
    }
`;

export const CREATE_VALUATION_NOTE = gql`
    mutation createValuationHistory($input: ValuationHistoryCreate!) {
        createValuationHistory(input: $input) {
            id
            status
            content
            contentType
            contentBy
            valuationId
            createdDate
        }
    }
`;

export const UPDATE_VALUATION = gql`
  mutation updateValuation($input: ValuationUpdate!) {
    updateValuation(input: $input) {
      id
      bank {
          id
          name
      }
      organisation {
          id
          name
          address
      }
      office {
          id
          name
          phone
          email
          address
          latitude
          longitude
      }
      company {
          id
          name
          offices {
              id
              name
              address
              latitude
              longitude
          }
      }
      requestedBy {
          id
          firstName
          lastName
      }
      underwriter {
          id
          firstName
          lastName
      }
      valuer {
        id
        role
        firstName
        lastName
        phone
        email
      }
      customerName
      customerEmail
      customerPhone
      securityAddress
      valuationType
      formType
      propertyLocationLatitude
      propertyLocationLongitude
      propertyAddress
      propertyNeighborhood
      propertyCity
      propertyRegion
      propertyCaseNumber
      propertyImageLinkUrl
      propertyImage {
        documentLocation
        filename
      }
      completedValuationDocument {
        documentLocation
        filename
      }
      dueDate
      status
      valuationAmount
      valuationCurrency
      broker
      brokerEmail
      notifyBroker
      sellingAgent
      sellingAgentEmail
      sellingAgentPhone
      expectedMarketValue
      inspectionDate
      notes
      valuationAmount
      slaPauseComment
      slaPaused
    }
  }
`

export const CHANGE_VALUATION_STATUS = gql`
  mutation changeValuationStatus ($input: ValuationStatusChange!) {
    changeValuationStatus(input: $input)
  }`;

export const ALL_VALUATIONS_BY_USER = gql`
  query ($statuses: [ValuationStatus], $offset: Int!, $count: Int!) {
    valuationsByUserType (statuses: $statuses, offset: $offset, count: $count) {
      totalValuationsAmount
      valuations {
        id
        bank {
          id
          name
        }
        requestedBy {
          id
          role
          firstName
          lastName
        }
        underwriter {
          id
          role
          firstName
          lastName
        }
        customerName
        securityAddress
        organisation {
          id
          name
          address
          status
          directAssign
        }
        company {
          id
          name
          status
        }
        office {
          id
          name
          phone
          email
        }
        valuer {
          id
          role
          firstName
          lastName
          active
        }
        valuationType
        formType
        propertyAddress
        propertyNeighborhood
        propertyCity
        propertyRegion
        propertyCaseNumber
        propertyImageLinkUrl
        overdueStatus
        propertyImage {
          filename
          documentLocation
        }
        completedValuationDocument {
          documentLocation
          filename
        }
        dueDate
        status
        valuationAmount
        valuationCurrency
        broker
        brokerEmail
        notifyBroker
        sellingAgent
        sellingAgentEmail
        sellingAgentPhone
        expectedMarketValue
        inspectionDate
        notes
        slaPauseComment
        slaPaused
        valuationHistoryList {
          id
          content
          status
          contentType
          contentBy
          createdDate
        }
      }
    }
  }
`;

// The banks a caller can filter the list by — those with a valuation they can see.
export const VALUATION_BANKS = gql`
  query valuationBanks {
    valuationBanks {
      id
      name
    }
  }
`;

export const GET_VALUATIONS_BY_USER = gql`
  query ($statuses: [ValuationStatus], $offset: Int!, $count: Int!, $searchTerm: String, $slaStatuses: [SlaStatus], $organisationIds: [Int], $companyIds:[Int], $bankIds: [Int], $sort: ValuationSort) {
    valuationsSearch (statuses: $statuses, offset: $offset, count: $count, searchTerm: $searchTerm, slaStatuses: $slaStatuses, organisationIds: $organisationIds, companyIds: $companyIds, bankIds: $bankIds, sort: $sort) {
      totalValuationsAmount
      valuations {
        id
        bank {
          name 
        }
        requestedBy {
          firstName 
          lastName
        }
        securityAddress
        organisation {
          name
        }
        company {
          name
        }
        valuer {
          firstName
          lastName
        }
        propertyAddress,
        propertyLocationLatitude
        propertyLocationLongitude
        propertyImageLinkUrl 
        overdueStatus
        completedValuationDocument {
          documentLocation
        }
        status 
        valuationAmount 
        sellingAgent
        inspectionDate
        slaPaused
        valuationHistoryList {
          status
          createdDate
        }
      }
    }
  }
`;

export const VALUATIONS_NUMBER = gql`
  query {
    valuationsNumbers {
      overdueCompletionValuations
      overdueInspectionValuations
      overdueNotAcceptedValuations
      pausedValuations
      valuationsByStatus {
        count
        status
      }
    }
  }
`;

export const VALUATIONS_BY_VALUER = gql`
  query valuationsByValuer($valuerId: Int!) {
    valuationsByValuer(valuerId: $valuerId) {
      id
      bank {
        id
        name
      }
      securityAddress
      status
      overdueStatus
      slaPaused
      valuationAmount
      inspectionDate
      propertyImageLinkUrl
      propertyImage {
        documentLocation
        filename
      }
      valuationHistoryList {
        id
        status
        createdDate
      }
    }
  }
`;

export const COMPANY_LIST_STATISTICS_QUERY = gql`
  query companyListStatistics {
    valuationsNumbers {
      valuationsByStatus {
        count
        status
      }
    }
    valuations(statuses: [APPROVED]) {
      id
      status
      valuationHistoryList {
        status
        createdDate
      }
    }
  }
`;